import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete
} from "@mui/material";
import { CommentDto, TradingCounterParty } from "../../../../../../api/Api";
import {
  useAssignees,
  useCounterpartyProgressOverride,
  useUpdateCounterparty
} from "../../../../dataHooks/counterpartyQueries";
import {
  createLifecycleActions,
  LifecycleAction
} from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../sharedComponents/commentsSection";
import { LifecycleDialog } from "../../sharedComponents/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificCounterparty: TradingCounterParty;
};

type FormData = {
  mandateApproval: string;
  mandateApprovalAssignee: string;
  comments: CommentDto[];
  signOffNote: string;
};

/** The mandate. The credit check moved to its own Credit Risk lifecycle. */
export const Risk: FC<Props> = ({ specificCounterparty }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("counterparty");
  const roles = useRoles();
  const progressOverride = useCounterpartyProgressOverride(
    specificCounterparty,
    "risk"
  );

  const [formData, setFormData] = useState<FormData>({
    mandateApproval: specificCounterparty.mandate
      ? "approved"
      : specificCounterparty?.riskLifecycle?.mandateApproval || "",
    mandateApprovalAssignee:
      specificCounterparty?.riskLifecycle?.mandateApprovalAssignee || "",
    signOffNote: specificCounterparty?.riskLifecycle?.signOffNote || "",
    comments: specificCounterparty?.riskLifecycle?.comments || []
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const riskAssignees = useAssignees("risk");

  const updateCounterparty = useUpdateCounterparty({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateCounterparty();

  const buildCounterparty = (
    values: FormData & { complete?: boolean },
    action: LifecycleAction = "save"
  ): TradingCounterParty => ({
    ...specificCounterparty,
    // Preserve the existing distinction: Save/comments update the root mandate, Complete does not.
    ...(action === "save"
      ? { mandate: values.mandateApproval?.toLowerCase() === "approved" }
      : {}),
    riskLifecycle: {
      ...specificCounterparty.riskLifecycle,
      ...values
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildCounterparty({ ...formData, comments })
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificCounterparty.progress?.risk,
    canSetInProgress: ownsLifecycle(roles, "risk"),
    buildEntity: (values, progress, action) => {
      const saved = buildCounterparty(values, action);
      return {
        ...saved,
        progress: { ...saved.progress, risk: progress }
      };
    },
    mutate: updateCounterparty.mutate
  });

  // endsWith, not includes: the Credit Risk route ends in "creditrisk".
  const open = location.pathname.endsWith("/risk");

  const canComplete = !!formData.mandateApproval;

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Risk"
      progressOverride={progressOverride}
      maxWidth="md"
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete,
        isSaving: updateCounterparty.isPending
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl sx={{ flex: 1 }} size="medium" required>
            <InputLabel>Mandate approval</InputLabel>
            <Select
              label="Mandate approval"
              value={formData.mandateApproval}
              onChange={(e) => handleChange("mandateApproval", e.target.value)}
            >
              <MenuItem value="initiated">
                <Typography variant="body1" component="span">
                  Initiated
                </Typography>
              </MenuItem>
              <MenuItem value="rejected">
                <Typography variant="body1" component="span">
                  Rejected
                </Typography>
              </MenuItem>
              <MenuItem value="approved">
                <Typography variant="body1" component="span">
                  Approved
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={riskAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              riskAssignees.data?.find(
                (a) => a.email === formData.mandateApprovalAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("mandateApprovalAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mandate approval assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
